import React, { useEffect, useState } from "react"
import { Container, Row, Col, Card, CardBody, CardTitle, Table, Badge, Button, Spinner, Alert, Progress } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { useParams, Link } from "react-router-dom"
import { 
  useGetTestByIdQuery,
  useLazyGetTestResultsQuery,
  useGetTestStatusQuery,
  useStartTestMutation,
  usePauseTestMutation,
  useCancelTestMutation
} from "../../store/tests/tests.api"
import io from "socket.io-client"
import { useEffect as useLayoutEffect, useRef } from "react"
import * as urls from "../../helpers/url_helper"

// Debug logging helper
const log = (action, data) => {
  console.log(`[FE-DEBUG-TestResults.js] ${new Date().toISOString()} - ${action}`, data || '');
};

const TestResults = () => {
  const { testId } = useParams()
  const [socket, setSocket] = useState(null)
  const [realTimeProgress, setRealTimeProgress] = useState(null)
  const [realTimeStatus, setRealTimeStatus] = useState(null)
  const [showSweetAlert, setShowSweetAlert] = useState(true)
  const [showPauseResumeButtons, setShowPauseResumeButtons] = useState(true)
  const [cancelError, setCancelError] = useState("")
  
  const { 
    data: test, 
    isLoading: testLoading, 
    error: testError, 
    refetch: refetchTest 
  } = useGetTestByIdQuery(testId)
  
  const { 
    data: results, 
    isLoading: resultsLoading, 
    error: resultsError, 
    refetch: refetchResults 
  } = useGetTestResultsQuery(testId)
  
  const {
    data: statusData,
    refetch: refetchStatus
  } = useGetTestStatusQuery(testId)
  
  const [cancelTest, { isLoading: cancelling }] = useCancelTestMutation()
  const [startTest, { isLoading: starting }] = useStartTestMutation()
  const [pauseTest, { isLoading: pausing }] = usePauseTestMutation()

  useEffect(() => {
    // Connect to socket for real-time updates
    const socketInstance = io(urls.SOCKET_URL);
    setSocket(socketInstance);
    
    socketInstance.on('connect', () => {
      console.log('Connected to socket server');
      socketInstance.emit('join:session', { sessionId: testId });
    });
    
    socketInstance.on('session:progress', (data) => {
      log('SOCKET_PROGRESS_RECEIVED', { testId, progress: data.progress });
      setRealTimeProgress(data.progress);
      setRealTimeStatus('running');
    });
    
    socketInstance.on('session:completed', () => {
      log('SOCKET_COMPLETED_RECEIVED', { testId });
      setRealTimeStatus('completed');
      refetchTest();
      refetchResults();
    });
    
    socketInstance.on('session:error', (data) => {
      log('SOCKET_ERROR_RECEIVED', { testId, error: data });
      console.error('Socket error:', data);
      refetchTest();
    });
    
    socketInstance.on('session:paused', () => {
      log('SOCKET_PAUSED_RECEIVED', { testId, currentStatus: realTimeStatus });
      setRealTimeStatus('paused');
      refetchTest();
    });

    return () => {
      socketInstance.disconnect();
    };
  }, [testId, refetchTest, refetchResults]);

  useEffect(() => {
    // Set up polling for test status updates if test is running
    let interval
    if (test && ['created', 'paused', 'running'].includes(test.status)) {
      log('POLLING_STARTED', { testId, status: test.status });
      interval = setInterval(() => {
        log('POLLING_REFETCH', { testId });
        refetchTest()
        refetchResults()
        refetchStatus()
      }, 5000)
    }

    return () => {
      if (interval) {
        log('POLLING_STOPPED', { testId });
        clearInterval(interval)
      }
    }
  }, [test, refetchTest, refetchResults, refetchStatus])

  // Log status changes
  useEffect(() => {
    if (test?.status) {
      log('TEST_STATUS_CHANGED', { testId, status: test.status, realTimeStatus });
    }
  }, [test?.status]);

  // Log real-time status changes
  useEffect(() => {
    if (realTimeStatus) {
      log('REALTIME_STATUS_CHANGED', { testId, realTimeStatus, testStatus: test?.status });
    }
  }, [realTimeStatus]);

  // Check if the results data has the expected structure
  const resultsData = results && results.results 
    ? results 
    : results && results.success 
      ? { 
          results: [], 
          stats: {} 
        } 
      : null;

  const handleCancelTest = async () => {
    log('CANCEL_TEST_CLICKED', { testId });
    try {
      if (showSweetAlert) {
        if (!window.confirm("Are you sure you want to stop this test?")) {
          log('CANCEL_TEST_CANCELLED_BY_USER', { testId });
          return;
        }
      }
      log('CANCEL_TEST_API_CALL_START', { testId });
      await cancelTest(testId)
      log('CANCEL_TEST_API_CALL_SUCCESS', { testId });
      refetchTest()
      setCancelError("")
    } catch (error) {
      log('CANCEL_TEST_ERROR', { testId, error: error.message });
      setCancelError(error.data?.message || error.message || "Failed to cancel test.")
    }
  }
  
  const handleStartTest = async () => {
    log('START_TEST_CLICKED', { testId });
    try {
      log('START_TEST_API_CALL_START', { testId });
      await startTest(testId)
      log('START_TEST_API_CALL_SUCCESS', { testId });
      refetchTest()
    } catch (error) {
      log('START_TEST_ERROR', { testId, error: error.message });
      console.error("Failed to start test:", error)
    }
  }

  const handlePauseTest = async () => {
    log('PAUSE_TEST_CLICKED', { testId });
    try {
      log('PAUSE_TEST_API_CALL_START', { testId });
      await pauseTest(testId)
      log('PAUSE_TEST_API_CALL_SUCCESS', { testId });
      refetchTest()
    } catch (error) {
      log('PAUSE_TEST_ERROR', { testId, error: error.message });
      console.error("Failed to pause test:", error)
    }
  }
  
  const handleExportCSV = () => {
    if (!resultsData || !resultsData.results) return
    
    // Create CSV content - matching UI table columns exactly
    const headers = ["Timestamp", "MSISDN", "Supplier", "SMSC", "Status", "Delivery Status", "Delivery Timestamp", "Message"]
    const csvRows = [
      headers.join(','),
      ...resultsData.results.map(result => [
        new Date(result.requestTime).toLocaleString(),
        result.msisdn,
        result.supplierName,
        result.smsc || '',
        result.status,
        result.deliveryStatus || '',
        result.deliveryTime ? new Date(result.deliveryTime).toLocaleString() : '',
        (result.message || '').replace(/,/g, ' ') // Replace commas to avoid CSV parsing issues
      ].join(','))
    ]
    
    const csvContent = csvRows.join('\n')
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `test-results-${testId}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const getStatusBadge = (status) => {
    // Override with real-time status if available
    status = realTimeStatus || status;
    
    switch (status) {
      case 'completed':
        return <Badge color="success">Completed</Badge>
      case 'stopped':
        return <Badge color="danger">Stopped</Badge>
      case 'created':
        return <Badge color="warning">Created</Badge>
      case 'running':
        return <Badge color="info">Running</Badge>
      case 'paused':
        return <Badge color="secondary">Paused</Badge>
      default:
        return <Badge color="primary">{status}</Badge>
    }
  }

  if (testLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Test Results" breadcrumbItem="Loading Test..." />
          <div className="text-center mt-5">
            <Spinner />
            <p className="mt-2">Loading test information...</p>
          </div>
        </Container>
      </div>
    )
  }

  if (testError) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Test Results" breadcrumbItem="Error" />
          <Alert color="danger">
            {testError.data?.message || "An error occurred while fetching test information"}
          </Alert>
          <div className="text-center mt-3">
            <Link to="/run-test" className="btn btn-primary">
              Back to Test Configuration
            </Link>
          </div>
        </Container>
      </div>
    )
  }

  // Calculate progress
  const progress = realTimeProgress !== null ? 
    realTimeProgress : 
    (statusData?.progress || test?.progress || 0);

  // Log button visibility
  const getButtonVisibility = () => {
    const visibility = {
      start: test?.status === 'created',
      resume: test?.status === 'paused',
      pause: test?.status === 'running',
      stop: ['running', 'paused'].includes(test?.status)
    };
    log('BUTTON_VISIBILITY_CALCULATED', { testId, status: test?.status, visibility });
    return visibility;
  };

  // Call button visibility logging
  const buttonVisibility = getButtonVisibility();

  return (
    <div className="page-content">
      <Container fluid>
        <div style={{ 
          fontSize: "12px", 
          color: "#811113", 
          fontWeight: "500", 
          marginBottom: "20px",
        }}>
          <span>TESTS</span>
          <span style={{ color: "#555", margin: "0 5px" }}>/</span>
          <span style={{ color: "#555" }}>{test?.name}</span>
        </div>
        
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <CardTitle>Test Details</CardTitle>
                  <div>
                    {test && test.status === 'created' && (
                      <Button 
                        color="success" 
                        className="me-2" 
                        onClick={handleStartTest}
                        disabled={starting}
                      >
                        {starting ? 'Starting...' : 'Start Test'}
                      </Button>
                    )}
                    {test && test.status === 'paused' && showPauseResumeButtons && (
                      <Button 
                        color="success" 
                        className="me-2" 
                        onClick={handleStartTest}
                        disabled={starting}
                      >
                        {starting ? 'Resuming...' : 'Resume'}
                      </Button>
                    )}
                    {test && test.status === 'running' && (
                      <Button 
                        color="warning" 
                        className="me-2" 
                        onClick={handlePauseTest}
                        disabled={pausing}
                      >
                        {pausing ? 'Pausing...' : 'Pause'}
                      </Button>
                    )}
                    {test && ['running', 'paused'].includes(test.status) && !['completed'].includes(test.status) && (
                      <Button 
                        color="danger" 
                        className="me-2" 
                        onClick={handleCancelTest}
                        disabled={cancelling}
                      >
                        {cancelling ? 'Stopping...' : 'Stop Test'}
                      </Button>
                    )}
                    <Link to="/run-test" className="btn btn-primary">
                      Run New Test
                    </Link>
                  </div>
                </div>

                {test && test.config && (
                  <div className="mb-4">
                    <div className="mb-3">
                      <h5 className="font-size-15">General Settings</h5>
                      <Row className="mb-3">
                        <Col md={6}>
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: "14px", color: "#555" }}>Customer</label>
                            <input 
                              type="text" 
                              className="form-control"
                              value={test.config.sender || '-'}
                              readOnly
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: "14px", color: "#555" }}>Operator</label>
                            <input 
                              type="text" 
                              className="form-control"
                              value={test.config?.operator || '-'}
                              readOnly
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: "14px", color: "#555" }}>Phone Numbers</label>
                            <div className="d-flex flex-wrap">
                              {test.config.msisdn1 && (
                                <div className="bg-light border rounded me-2 mb-2" style={{ 
                                  padding: "4px 10px",
                                  fontSize: "14px",
                                  color: "#333"
                                }}>
                                  {test.config.msisdn1}
                                </div>
                              )}
                              {test.config.msisdn2 && (
                                <div className="bg-light border rounded me-2 mb-2" style={{ 
                                  padding: "4px 10px",
                                  fontSize: "14px",
                                  color: "#333"
                                }}>
                                  {test.config.msisdn2}
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="mb-3">
                      <h5 className="font-size-15">Message Settings</h5>
                      <Row className="mb-3">
                        <Col md={6}>
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: "14px", color: "#555" }}>Template</label>
                            <input 
                              type="text" 
                              className="form-control"
                              value={test.config?.template || '-'}
                              readOnly
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: "14px", color: "#555" }}>Language</label>
                            <input 
                              type="text" 
                              className="form-control"
                              value={test.config?.language || 'English'}
                              readOnly
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: "14px", color: "#555" }}>Message Text</label>
                            <textarea 
                              className="form-control"
                              value={test.config.text || '-'}
                              readOnly
                              rows={3}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <div className="d-flex">
                            <div className="me-4">
                              <small style={{ color: "#666" }}>Format: <strong>{test.config.code_format || 'Numeric'}</strong></small>
                            </div>
                            <div className="me-4">
                              <small style={{ color: "#666" }}>Type: <strong>{test.config.type || 'Mixed'}</strong></small>
                            </div>
                            <div>
                              <small style={{ color: "#666" }}>Code Length: <strong>{test.config.length || '6'}</strong></small>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div>
                      <h5 className="font-size-15">Route Settings</h5>
                      <Row>
                        <Col md={12}>
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: "14px", color: "#555" }}>Routes</label>
                            <div className="d-flex flex-wrap">
                              {test.config.routeselection1 && (
                                <div className="bg-light border rounded me-2 mb-2" style={{ 
                                  padding: "4px 10px",
                                  fontSize: "14px",
                                  color: "#333"
                                }}>
                                  {test.config.routeselection1}
                                </div>
                              )}
                              {test.config.routeselection2 && (
                                <div className="bg-light border rounded me-2 mb-2" style={{ 
                                  padding: "4px 10px",
                                  fontSize: "14px",
                                  color: "#333"
                                }}>
                                  {test.config.routeselection2}
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}
                
                <Table bordered responsive>
                  <tbody>
                    <tr>
                      <th style={{ width: '200px' }}>Test Name</th>
                      <td>{test?.name}</td>
                    </tr>
                    <tr>
                      <th>Test ID</th>
                      <td>{test?.id}</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>{getStatusBadge(test?.status)}</td>
                    </tr>
                    <tr>
                      <th>Progress</th>
                      <td>
                        <Progress value={progress} color="info" className="mb-2">
                          {progress}%
                        </Progress>
                      </td>
                    </tr>
                    <tr>
                      <th>Started At</th>
                      <td>{test?.createdAt ? new Date(test.createdAt).toLocaleString() : '-'}</td>
                    </tr>
                    <tr>
                      <th>Last Updated</th>
                      <td>{test?.updatedAt ? new Date(test.updatedAt).toLocaleString() : '-'}</td>
                    </tr>
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
        
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Test Results</CardTitle>
                
                {resultsLoading && !resultsData && (
                  <div className="text-center">
                    <Spinner size="sm" />
                    <p>Loading results...</p>
                  </div>
                )}
                
                {resultsError && (
                  <Alert color="danger">
                    {resultsError.data?.message || "An error occurred while fetching test results"}
                  </Alert>
                )}
                
                {test && ['created', 'running', 'paused'].includes(test.status) && (
                      <Alert color="info">
                    {test.status === 'created' ? 
                      "Test is ready to run. Click 'Start Test' to begin." : 
                      test.status === 'running' ? 
                        "Test is currently running. Results will update automatically." : 
                        "Test is paused. Results shown are partial."}
                      </Alert>
                    )}
                    
                {test?.status === 'stopped' && (
                      <Alert color="warning">
                    This test was stopped before completion.
                      </Alert>
                    )}
                    
                {!resultsLoading && resultsData && (
                      <div className="mt-4">
                    <div className="d-flex justify-content-end mb-3">
                      <Button 
                        color="primary" 
                        size="sm"
                        onClick={handleExportCSV}
                        disabled={!resultsData.results.length}
                      >
                        Export CSV
                      </Button>
                    </div>
                        <Table bordered responsive>
                          <thead>
                            <tr>
                          <th>Timestamp</th>
                          <th>MSISDN</th>
                          <th>Supplier</th>
                          <th>SMSC</th>
                              <th>Status</th>
                          <th>Delivery Status</th>
                          <th>Delivery Timestamp</th>
                          <th>Message</th>
                            </tr>
                          </thead>
                          <tbody>
                        {resultsData.results.map((result, idx) => (
                          <tr key={idx}>
                            <td>{new Date(result.requestTime).toLocaleString()}</td>
                            <td>{result.msisdn}</td>
                            <td>{result.supplierName}</td>
                            <td>{result.smsc || '-'}</td>
                            <td>
                              <Badge color={result.status === 'delivered' ? 'success' : 
                                           result.status === 'failed' ? 'danger' : 
                                           result.status === 'pending' ? 'warning' : 'secondary'}>
                                {result.status}
                              </Badge>
                            </td>
                            <td>{result.deliveryStatus || '-'}</td>
                            <td>{result.deliveryTime ? new Date(result.deliveryTime).toLocaleString() : '-'}</td>
                            <td>{result.message || '-'}</td>
                              </tr>
                            ))}
                        {(!resultsData.results || resultsData.results.length === 0) && (
                          <tr>
                            <td colSpan="8" className="text-center">No results available yet</td>
                          </tr>
                        )}
                          </tbody>
                        </Table>
                    
                    {resultsData.stats && Object.keys(resultsData.stats).length > 0 && (
                      <div className="mt-4">
                        <h5>Summary</h5>
                        <Row>
                          {Object.entries(resultsData.stats).map(([status, count]) => (
                            <Col md={3} key={status}>
                              <Card body>
                                <div className="d-flex align-items-center">
                                  <div style={{
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%', 
                                    backgroundColor: status === 'delivered' ? '#28a745' :
                                                    status === 'failed' ? '#dc3545' : 
                                                    status === 'pending' ? '#ffc107' : '#6c757d',
                                    marginRight: 8
                                  }}></div>
                                  <div>
                                    <h5 className="mb-0">{count}</h5>
                                    <small>{status}</small>
                                  </div>
                                </div>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {test && (test.status === 'paused' || test.status === 'running') && (
          <Row className="mt-4">
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">Information</CardTitle>
                  
                  <div className="mb-3">
                    {test.status === 'paused' && (
                      <div className="d-flex align-items-center mb-3">
                        <span>When paused </span>
                        <Badge color="secondary" className="ms-2">✓</Badge>
                        
                        {showPauseResumeButtons && (
                          <Button 
                            color="primary" 
                            size="sm" 
                            className="ms-3"
                            onClick={handleStartTest}
                            disabled={starting}
                          >
                            Resume
                          </Button>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-3">
                      <div className="form-check mb-2">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="showSweetAlert" 
                          checked={showSweetAlert} 
                          onChange={(e) => setShowSweetAlert(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="showSweetAlert">
                          Show a sweet alert confirmation when cancelling a test.
                        </label>
                      </div>
                      
                      <div className="form-check">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="showPauseResume" 
                          checked={showPauseResumeButtons} 
                          onChange={(e) => setShowPauseResumeButtons(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="showPauseResume">
                          Conditionally show pause and resume buttons.
                        </label>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {cancelError && (
          <Alert color="danger" className="mt-2">
            {cancelError}
          </Alert>
        )}
      </Container>
    </div>
  )
}

export default TestResults 